import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, TrendingDown, TrendingUp, Wallet, Camera, Upload, Loader2, Sparkles, Download, Calendar, BarChart3 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { api, API_BASE, fmtEur, fmtDate, todayISO } from "../lib/api";
import { toast } from "sonner";

const PIE_COLORS = ["#2C4C3B", "#C86A51", "#4A7C59", "#E9A846", "#1E3529", "#A9533E", "#8DA88F", "#D97706"];

const CATEGORIE = [
    "Acquisto merce",
    "Utenze",
    "Affitto",
    "Trasporti",
    "Materiali pulizia",
    "Manutenzione",
    "Tasse",
    "Altro",
];

const METODI = ["Contanti", "Carta", "Bonifico", "POS", "Assegno"];

function monthRange(month) {
    const [y, m] = month.split("-").map(Number);
    const start = `${month}-01`;
    const endMonth = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
    const endLast = new Date(new Date(`${endMonth}-01`) - 86400000).toISOString().slice(0, 10);
    return { start, end: endLast };
}

export default function Spese() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [viewMode, setViewMode] = useState("mese"); // "mese" | "anno"
    const [year, setYear] = useState(new Date().getFullYear());
    const [yearly, setYearly] = useState(null);
    const [supplierStats, setSupplierStats] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        data: todayISO(),
        descrizione: "",
        importo: "",
        categoria: "Altro",
        metodo_pagamento: "Contanti",
        fornitore: "",
        note: "",
        tipo: "spesa",
    });
    const fileRef = useRef(null);
    const cameraRef = useRef(null);
    const [scanning, setScanning] = useState(false);

    async function scanReceipt(file) {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Carica una foto (jpg/png)");
            return;
        }
        setScanning(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("auto_expense", "true");
        try {
            const r = await api.post("/invoices/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            toast.success("Spesa registrata da scontrino", {
                description: `${r.data.fornitore || "Fornitore"} · ${fmtEur(r.data.totale)}`,
            });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore analisi AI");
        } finally {
            setScanning(false);
            if (fileRef.current) fileRef.current.value = "";
            if (cameraRef.current) cameraRef.current.value = "";
        }
    }

    async function load() {
        setLoading(true);
        try {
            const { start, end } = monthRange(month);
            const [it, sm] = await Promise.all([
                api.get("/expenses", { params: { start, end } }),
                api.get("/expenses/summary", { params: { month } }),
            ]);
            setItems(it.data);
            setSummary(sm.data);
        } catch {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [month]);

    useEffect(() => {
        if (viewMode === "anno") {
            api.get("/expenses/yearly", { params: { year } })
                .then((r) => setYearly(r.data))
                .catch(() => setYearly(null));
            api.get("/suppliers/stats", { params: { start: `${year}-01-01`, end: `${year}-12-31` } })
                .then((r) => setSupplierStats(r.data))
                .catch(() => setSupplierStats(null));
        }
    }, [viewMode, year]);

    async function submit() {
        if (!form.descrizione || !form.importo) {
            toast.error("Descrizione e importo obbligatori");
            return;
        }
        try {
            await api.post("/expenses", {
                ...form,
                importo: parseFloat(form.importo),
            });
            toast.success("Registrato");
            setOpen(false);
            setForm({ ...form, descrizione: "", importo: "", note: "" });
            load();
        } catch {
            toast.error("Errore");
        }
    }

    async function del(id) {
        if (!confirm("Eliminare?")) return;
        await api.delete(`/expenses/${id}`);
        toast.success("Eliminato");
        load();
    }

    return (
        <div className="space-y-6 pb-6" data-testid="spese-page">
            <section className="card-soft p-6 bg-gradient-to-br from-[#FFFCF6] to-[#F4EFE0] border-[#E4DEC3]" data-testid="quick-scan-section">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-12 h-12 rounded-2xl bg-accent-warm grid place-items-center text-white shadow-md shrink-0">
                        <Sparkles size={22} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <h2 className="font-display text-lg font-semibold text-forest-dark">
                            Scontrino o fattura con foto
                        </h2>
                        <p className="text-sm text-stone-600 mt-1">
                            Scatta o carica una foto: l'AI registra automaticamente la spesa in contabilità.
                        </p>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => cameraRef.current?.click()}
                        disabled={scanning}
                        className="btn-accent inline-flex items-center gap-2 text-sm"
                        data-testid="spese-btn-camera"
                    >
                        {scanning ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                        Scatta foto
                    </button>
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={scanning}
                        className="btn-ghost inline-flex items-center gap-2 text-sm"
                        data-testid="spese-btn-upload"
                    >
                        {scanning ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                        Galleria foto
                    </button>
                    <input
                        ref={cameraRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => scanReceipt(e.target.files?.[0])}
                        data-testid="spese-input-camera"
                    />
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => scanReceipt(e.target.files?.[0])}
                        data-testid="spese-input-file"
                    />
                </div>
                {scanning && (
                    <p className="text-xs text-stone-500 mt-3 italic">L'AI sta leggendo il documento...</p>
                )}
            </section>

            <div className="card-soft p-5 flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="inline-flex bg-bone-2 rounded-lg p-1">
                        <button
                            onClick={() => setViewMode("mese")}
                            className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1 transition ${viewMode === "mese" ? "bg-white text-forest shadow-sm" : "text-stone-500"}`}
                            data-testid="view-mese"
                        >
                            <Calendar size={14} /> Mese
                        </button>
                        <button
                            onClick={() => setViewMode("anno")}
                            className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1 transition ${viewMode === "anno" ? "bg-white text-forest shadow-sm" : "text-stone-500"}`}
                            data-testid="view-anno"
                        >
                            <BarChart3 size={14} /> Anno
                        </button>
                    </div>
                    {viewMode === "mese" ? (
                        <input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            className="input-soft max-w-[180px]"
                            data-testid="month-picker"
                        />
                    ) : (
                        <input
                            type="number"
                            value={year}
                            min="2000"
                            max="2099"
                            onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
                            className="input-soft max-w-[120px]"
                            data-testid="year-picker"
                        />
                    )}
                </div>
                <button
                    onClick={() => setOpen(true)}
                    className="btn-primary inline-flex items-center gap-2 text-sm"
                    data-testid="btn-new-expense"
                >
                    <Plus size={16} /> Nuovo movimento
                </button>
                <a
                    href={viewMode === "anno"
                        ? `${API_BASE}/exports/expenses.csv?start=${year}-01-01&end=${year}-12-31`
                        : `${API_BASE}/exports/expenses.csv?start=${monthRange(month).start}&end=${monthRange(month).end}`}
                    className="btn-ghost inline-flex items-center gap-2 text-sm"
                    data-testid="btn-export-csv"
                    download
                >
                    <Download size={16} /> Esporta CSV
                </a>
            </div>

            {viewMode === "mese" && summary && (
                <div className="grid sm:grid-cols-3 gap-4 stagger" data-testid="summary-cards">
                    <div className="card-soft p-5">
                        <div className="w-10 h-10 rounded-xl bg-orange-50 text-terracotta-dark grid place-items-center">
                            <TrendingDown size={18} />
                        </div>
                        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-3">Spese</p>
                        <p className="font-display text-2xl font-semibold text-forest-dark mt-1">{fmtEur(summary.spese_totali)}</p>
                    </div>
                    <div className="card-soft p-5">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
                            <TrendingUp size={18} />
                        </div>
                        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-3">Incassi</p>
                        <p className="font-display text-2xl font-semibold text-forest-dark mt-1">{fmtEur(summary.incassi_totali)}</p>
                    </div>
                    <div className={`card-soft p-5 ${summary.saldo >= 0 ? "" : "bg-red-50/30"}`}>
                        <div className={`w-10 h-10 rounded-xl grid place-items-center ${summary.saldo >= 0 ? "bg-forest-50 text-forest" : "bg-red-100 text-red-700"}`}>
                            <Wallet size={18} />
                        </div>
                        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-3">Saldo</p>
                        <p className={`font-display text-2xl font-semibold mt-1 ${summary.saldo >= 0 ? "text-forest-dark" : "text-red-700"}`}>{fmtEur(summary.saldo)}</p>
                        {summary.incassi_totali > 0 && (
                            <p className="text-xs text-stone-500 mt-1">
                                Margine lordo: <span className={`font-semibold ${summary.margine_lordo >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtEur(summary.margine_lordo)} ({summary.margine_percentuale}%)</span>
                            </p>
                        )}
                    </div>
                </div>
            )}

            {viewMode === "mese" && summary?.per_categoria?.length > 0 && (
                <section className="card-soft p-6" data-testid="category-chart">
                    <h2 className="font-display text-xl font-semibold text-forest-dark mb-4">Spese per categoria</h2>
                    <ul className="space-y-3">
                        {summary.per_categoria.map((c) => {
                            const pct = summary.spese_totali ? (c.totale / summary.spese_totali) * 100 : 0;
                            return (
                                <li key={c.categoria}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-semibold text-forest-dark">{c.categoria}</span>
                                        <span className="text-stone-600">{fmtEur(c.totale)} <span className="text-stone-400 text-xs">({pct.toFixed(0)}%)</span></span>
                                    </div>
                                    <div className="h-2 bg-bone-2 rounded-full overflow-hidden">
                                        <div className="h-full bg-forest" style={{ width: `${pct}%` }} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            <section className={`card-soft p-6 ${viewMode === "anno" ? "hidden" : ""}`} data-testid="movements-list">
                <h2 className="font-display text-xl font-semibold text-forest-dark mb-4">
                    Movimenti del mese ({items.length})
                </h2>
                {loading ? (
                    <div className="h-20 bg-bone-2 rounded-xl animate-pulse" />
                ) : !items.length ? (
                    <p className="text-stone-500 text-center py-10 text-sm">Nessun movimento per questo mese.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-soft">
                                    <th className="py-2 pr-2">Data</th>
                                    <th className="py-2 pr-2">Descrizione</th>
                                    <th className="py-2 pr-2 hidden md:table-cell">Categoria</th>
                                    <th className="py-2 pr-2 hidden sm:table-cell">Metodo</th>
                                    <th className="py-2 pr-2 text-right">Importo</th>
                                    <th className="py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((x) => (
                                    <tr key={x.id} className="border-b border-soft/60 hover:bg-bone-2/40" data-testid={`expense-row-${x.id}`}>
                                        <td className="py-3 pr-2 text-stone-700">{fmtDate(x.data)}</td>
                                        <td className="py-3 pr-2">
                                            <p className="font-semibold text-forest-dark">{x.descrizione}</p>
                                            {x.fornitore && <p className="text-xs text-stone-500">{x.fornitore}</p>}
                                        </td>
                                        <td className="py-3 pr-2 hidden md:table-cell">
                                            <span className="chip bg-bone-2 text-stone-700">{x.categoria}</span>
                                        </td>
                                        <td className="py-3 pr-2 hidden sm:table-cell text-stone-600">{x.metodo_pagamento}</td>
                                        <td className={`py-3 pr-2 text-right font-semibold ${x.tipo === "incasso" ? "text-emerald-700" : "text-terracotta-dark"}`}>
                                            {x.tipo === "incasso" ? "+" : "-"} {fmtEur(x.importo)}
                                        </td>
                                        <td className="py-3 text-right">
                                            <button
                                                onClick={() => del(x.id)}
                                                className="p-2 text-stone-400 hover:text-red-600 rounded-lg"
                                                data-testid={`del-exp-${x.id}`}
                                                aria-label="Elimina"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {viewMode === "anno" && yearly && (
                <div className="space-y-6" data-testid="yearly-view">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
                        <div className="card-soft p-5">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
                                <TrendingUp size={18} />
                            </div>
                            <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-3">Totale incassi {yearly.year}</p>
                            <p className="font-display text-2xl font-semibold text-forest-dark mt-1">{fmtEur(yearly.incassi_totali)}</p>
                        </div>
                        <div className="card-soft p-5">
                            <div className="w-10 h-10 rounded-xl bg-orange-50 text-terracotta-dark grid place-items-center">
                                <TrendingDown size={18} />
                            </div>
                            <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-3">Totale spese {yearly.year}</p>
                            <p className="font-display text-2xl font-semibold text-forest-dark mt-1">{fmtEur(yearly.spese_totali)}</p>
                            <p className="text-xs text-stone-500 mt-0.5">di cui <span className="font-semibold">{fmtEur(yearly.costo_merce)}</span> di merce</p>
                        </div>
                        <div className={`card-soft p-5 ${yearly.margine_lordo >= 0 ? "bg-gradient-to-br from-emerald-50/30 to-white" : "bg-red-50/30"}`} data-testid="kpi-margine">
                            <div className={`w-10 h-10 rounded-xl grid place-items-center ${yearly.margine_lordo >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                <BarChart3 size={18} />
                            </div>
                            <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-3">Margine lordo</p>
                            <p className={`font-display text-2xl font-semibold mt-1 ${yearly.margine_lordo >= 0 ? "text-forest-dark" : "text-red-700"}`}>{fmtEur(yearly.margine_lordo)}</p>
                            <p className="text-xs text-stone-500 mt-0.5">{yearly.margine_percentuale}% sui ricavi · incassi − costo merce</p>
                        </div>
                        <div className={`card-soft p-5 ${yearly.saldo >= 0 ? "" : "bg-red-50/30"}`}>
                            <div className={`w-10 h-10 rounded-xl grid place-items-center ${yearly.saldo >= 0 ? "bg-forest-50 text-forest" : "bg-red-100 text-red-700"}`}>
                                <Wallet size={18} />
                            </div>
                            <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-3">Saldo netto</p>
                            <p className={`font-display text-2xl font-semibold mt-1 ${yearly.saldo >= 0 ? "text-forest-dark" : "text-red-700"}`}>{fmtEur(yearly.saldo)}</p>
                            <p className="text-xs text-stone-500 mt-0.5">incassi − tutte le spese</p>
                        </div>
                    </div>

                    <section className="card-soft p-6" data-testid="yearly-monthly-chart">
                        <h2 className="font-display text-xl font-semibold text-forest-dark mb-4">
                            Andamento mensile {yearly.year}
                        </h2>
                        <div className="h-72 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={yearly.per_mese} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" vertical={false} />
                                    <XAxis dataKey="mese" tick={{ fontSize: 11, fill: "#5C5C5C" }} axisLine={{ stroke: "#E5E2D9" }} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#5C5C5C" }} axisLine={{ stroke: "#E5E2D9" }} tickLine={false} />
                                    <Tooltip
                                        contentStyle={{ background: "#fff", border: "1px solid #E5E2D9", borderRadius: 8, fontSize: 12 }}
                                        formatter={(v) => fmtEur(v)}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar dataKey="incassi" name="Incassi" fill="#4A7C59" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="spese" name="Spese" fill="#C86A51" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </section>

                    <div className="grid lg:grid-cols-2 gap-6">
                        <section className="card-soft p-6" data-testid="yearly-pie">
                            <h2 className="font-display text-xl font-semibold text-forest-dark mb-4">
                                Spese per categoria
                            </h2>
                            {yearly.per_categoria_spese.length === 0 ? (
                                <p className="text-sm text-stone-500 text-center py-10">Nessuna spesa nell'anno.</p>
                            ) : (
                                <>
                                    <div className="h-64 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={yearly.per_categoria_spese}
                                                    dataKey="totale"
                                                    nameKey="categoria"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={90}
                                                    innerRadius={50}
                                                    paddingAngle={2}
                                                    isAnimationActive={false}
                                                >
                                                    {yearly.per_categoria_spese.map((_, i) => (
                                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    formatter={(v) => fmtEur(v)}
                                                    contentStyle={{ background: "#fff", border: "1px solid #E5E2D9", borderRadius: 8, fontSize: 12 }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <ul className="mt-3 space-y-1.5">
                                        {yearly.per_categoria_spese.map((c, i) => (
                                            <li key={c.categoria} className="flex items-center justify-between text-sm">
                                                <span className="inline-flex items-center gap-2 text-stone-700">
                                                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                                                    {c.categoria}
                                                </span>
                                                <span className="font-semibold text-forest">{fmtEur(c.totale)} <span className="text-stone-400 text-xs">({yearly.spese_totali ? ((c.totale / yearly.spese_totali) * 100).toFixed(0) : 0}%)</span></span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </section>

                        <section className="card-soft p-6" data-testid="yearly-suppliers">
                            <h2 className="font-display text-xl font-semibold text-forest-dark mb-4">
                                Top fornitori
                            </h2>
                            {yearly.top_fornitori.length === 0 ? (
                                <p className="text-sm text-stone-500 text-center py-10">Nessun fornitore registrato.</p>
                            ) : (
                                <ul className="space-y-2.5">
                                    {yearly.top_fornitori.map((f, i) => {
                                        const pct = yearly.spese_totali ? (f.totale / yearly.spese_totali) * 100 : 0;
                                        return (
                                            <li key={f.fornitore}>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="font-semibold text-forest-dark inline-flex items-center gap-2">
                                                        <span className="text-stone-400 text-xs">#{i + 1}</span>
                                                        {f.fornitore}
                                                    </span>
                                                    <span className="text-stone-600">{fmtEur(f.totale)}</span>
                                                </div>
                                                <div className="h-2 bg-bone-2 rounded-full overflow-hidden">
                                                    <div className="h-full bg-forest" style={{ width: `${pct}%` }} />
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </section>
                    </div>

                    <p className="text-xs text-stone-500 text-center italic">
                        💡 Esporta il CSV annuale con il pulsante "Esporta CSV" in alto e mandalo al commercialista.
                    </p>

                    {supplierStats?.fornitori?.length > 0 && (
                        <section className="card-soft p-6" data-testid="supplier-stats-section">
                            <h2 className="font-display text-xl font-semibold text-forest-dark mb-1">
                                Dettaglio fornitori {yearly.year}
                            </h2>
                            <p className="text-sm text-stone-500 mb-4">
                                Stato di ciascun fornitore: totale speso, n° movimenti, importo medio, ultima fattura ricevuta e ripartizione per categoria.
                            </p>
                            <div className="space-y-3">
                                {supplierStats.fornitori.map((f, i) => (
                                    <div key={f.fornitore} className="rounded-xl border border-soft p-4" data-testid={`supplier-${i}`}>
                                        <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                                            <div>
                                                <p className="font-display font-semibold text-forest-dark text-base">
                                                    <span className="text-stone-400 text-xs mr-2">#{i + 1}</span>
                                                    {f.fornitore}
                                                </p>
                                                <p className="text-xs text-stone-500 mt-0.5">
                                                    Ultima fattura: {fmtDate(f.ultima_data)} · {f.numero_movimenti} movimenti
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-display text-xl font-semibold text-forest">{fmtEur(f.totale_spese)}</p>
                                                <p className="text-xs text-stone-500">{f.percentuale_su_totale}% del tuo totale spese</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div className="bg-bone-2 rounded-lg p-2">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Media a movimento</p>
                                                <p className="font-semibold text-forest-dark mt-0.5">{fmtEur(f.media_movimento)}</p>
                                            </div>
                                            <div className="bg-bone-2 rounded-lg p-2">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Categorie</p>
                                                <p className="font-semibold text-forest-dark mt-0.5">{f.per_categoria.length}</p>
                                            </div>
                                        </div>
                                        {f.per_categoria.length > 0 && (
                                            <details className="mt-2">
                                                <summary className="cursor-pointer text-xs text-forest font-semibold">
                                                    Mostra ripartizione per categoria
                                                </summary>
                                                <ul className="mt-2 space-y-1">
                                                    {f.per_categoria.map((c) => (
                                                        <li key={c.categoria} className="flex justify-between text-xs">
                                                            <span className="text-stone-700">{c.categoria}</span>
                                                            <span className="font-semibold text-forest">{fmtEur(c.totale)}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </details>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}

            {open && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)} data-testid="expense-modal">
                    <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-soft flex items-center justify-between">
                            <h3 className="font-display text-lg font-semibold text-forest-dark">Nuovo movimento</h3>
                        </div>
                        <div className="p-6 space-y-3">
                            <div className="flex gap-2" role="tablist">
                                {["spesa", "incasso"].map((t) => (
                                    <button
                                        key={t}
                                        onClick={() => setForm({ ...form, tipo: t })}
                                        data-testid={`type-${t}`}
                                        className={`flex-1 py-2 rounded-xl text-sm font-semibold capitalize transition ${form.tipo === t ? "bg-forest text-white" : "bg-bone-2 text-stone-600"}`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Descrizione *</span>
                                <input className="input-soft mt-1" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="exp-desc" />
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Importo € *</span>
                                    <input type="number" step="0.01" className="input-soft mt-1" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="exp-amount" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Data</span>
                                    <input type="date" className="input-soft mt-1" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="exp-date" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Categoria</span>
                                    <select className="input-soft mt-1" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} data-testid="exp-cat">
                                        {CATEGORIE.map((c) => <option key={c}>{c}</option>)}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Metodo</span>
                                    <select className="input-soft mt-1" value={form.metodo_pagamento} onChange={(e) => setForm({ ...form, metodo_pagamento: e.target.value })} data-testid="exp-method">
                                        {METODI.map((m) => <option key={m}>{m}</option>)}
                                    </select>
                                </label>
                            </div>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Fornitore (opz.)</span>
                                <input className="input-soft mt-1" value={form.fornitore} onChange={(e) => setForm({ ...form, fornitore: e.target.value })} data-testid="exp-supplier" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Note</span>
                                <textarea className="input-soft mt-1" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="exp-note" />
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button onClick={() => setOpen(false)} className="btn-ghost text-sm flex-1" data-testid="exp-cancel">Annulla</button>
                                <button onClick={submit} className="btn-primary text-sm flex-1" data-testid="exp-save">Salva</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
